<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { t } from '$lib/stores/i18n';

	const dispatch = createEventDispatcher();

	export let isOpen = false;
	export let techType: 'FDM' | 'SLA' | 'MJF' | 'SLM' = 'FDM';

	const techData = {
		FDM: {
			title: 'FDM',
			guidelines: [
				{ featureKey: 'unsupportedWalls', size: '0.8 mm (0.0315")' },
				{ featureKey: 'supportedWalls', size: '0.8 mm (0.0315")' },
				{ featureKey: 'minimumFeatureSize', size: '2.0 mm (0.00787")' },
				{ featureKey: 'minimumHoleDiameter', size: '2.0 mm (0.00787")' }
			]
		},
		SLA: {
			title: 'SLA',
			guidelines: [
				{ featureKey: 'unsupportedWalls', size: '1.0 mm (0.0393")' },
				{ featureKey: 'supportedWalls', size: '0.5 mm (0.0197")' },
				{ featureKey: 'minimumFeatureSize', size: '0.2 mm (0.00787")' },
				{ featureKey: 'minimumHoleDiameter', size: '0.5 mm (0.0197")' },
				{ featureKey: 'minimumEscapeHoleDiameter', size: '4.0 mm (0.157")' }
			]
		},
		MJF: {
			title: 'MJF',
			guidelines: [
				{ featureKey: 'unsupportedWalls', size: '1.0 mm (0.040")' },
				{ featureKey: 'supportedWalls', size: '0.7 mm (0.030")' },
				{ featureKey: 'minimumFeatureSize', size: '0.25 mm (0.010")' },
				{ featureKey: 'minimumHoleDiameter', size: '1.0 mm (0.040")' }
			]
		},
		SLM: {
			title: 'SLM',
			guidelines: [
				{ featureKey: 'unsupportedWalls', size: '0.4 mm (0.016")' },
				{ featureKey: 'supportedWalls', size: '0.2 mm (0.008")' },
				{ featureKey: 'minimumFeatureSize', size: '0.1 mm (0.004")' },
				{ featureKey: 'minimumHoleDiameter', size: '0.3 mm (0.012")' },
				{ featureKey: 'minimumEscapeHoleDiameter', size: '2.0 mm (0.079")' }
			]
		}
	};

	$: currentTech = techData[techType];
	$: techKey = techType.toLowerCase() as 'fdm' | 'sla' | 'mjf' | 'slm';

	$: if (techType) {
		console.log('Current tech data:', techType);
	}

	function closePopup() {
		isOpen = false;
		dispatch('close');
	}

	function handleBackdropClick(event: MouseEvent) {
		if (event.target === event.currentTarget) {
			closePopup();
		}
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Escape') {
			closePopup();
		}
	}
</script>

<svelte:window on:keydown={handleKeydown} />

{#if isOpen}
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<div
		class="popup-overlay"
		on:click={handleBackdropClick}
		role="dialog"
		aria-modal="true"
		tabindex="-1"
	>
		<div class="popup-content">
			<button class="close-btn" on:click={closePopup} aria-label="Close popup">
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					stroke-width="2"
				>
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			</button>

			<div class="popup-header">
				<h2 class="tech-title">{currentTech.title}</h2>
				<h3 class="tech-full-name">{$t(`technology.${techKey}.fullName`)}</h3>
			</div>

			<div class="popup-body">
				<p class="tech-description">{$t(`technology.${techKey}.description`)}</p>

				<div class="guidelines-section">
					<div class="row">
						<div class="col-5">
							<img
								src={`/assets/img/icons/${currentTech.title.toLowerCase()}.png`}
								alt={`${currentTech.title} icon`}
								class="tech-icon"
							/>
						</div>
						<div class="col-7">
							<h4 class="guidelines-title">
								<img
									src="/assets/img/icons/btn_right_arrow.svg"
									alt="icon"
									class="right_arrow"
									style="height: 24px; margin-right: 5px;"
								/>
								{$t('technology.designGuidelines')} {currentTech.title}
							</h4>

							<p class="guidelines-subtitle">
								{$t('technology.guidelinesSubtitle')}
							</p>

							<div class="guidelines-table">
								<div class="table-header">
									<div class="table-cell">{$t('technology.table.feature')}</div>
									<div class="table-cell">{$t('technology.table.recommendedSize')}</div>
								</div>
								{#each currentTech.guidelines as guideline}
									<div class="table-row">
										<div class="table-cell feature-name">{$t(`technology.features.${guideline.featureKey}`)}</div>
										<div class="table-cell feature-size">{guideline.size}</div>
									</div>
								{/each}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	.popup-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		display: flex;
		align-items: start;
		justify-content: center;
		z-index: 1000;
		padding: 1rem;
	}

	.popup-content {
		background: #1a1a1a;
		border-radius: 1rem;
		padding: 2rem;
		max-width: 70rem;
		max-height: 80vh;
		width: 100%;
		position: relative;
		overflow-y: auto;
		border: 1px solid #333;
		margin-top: 11vh;
		height: 80vh;
	}

	.close-btn {
		position: absolute;
		top: 1rem;
		right: 1rem;
		background: none;
		border: none;
		color: #888;
		cursor: pointer;
		padding: 0.5rem;
		border-radius: 0.5rem;
		transition: all 0.3s ease;
	}

	.close-btn:hover {
		background: #333;
		color: #fff;
	}

	.popup-header {
		margin-bottom: 1.5rem;
	}

	.tech-title {
		font-size: 2.5rem;
		font-weight: bold;
		color: #00ff88;
		margin: 0 0 0.5rem 0;
	}

	.tech-full-name {
		font-size: 1.2rem;
		color: #888;
		margin: 0;
		font-weight: normal;
	}

	.popup-body {
		color: #ccc;
		line-height: 1.6;
	}

	.tech-description {
		font-size: 1rem;
		margin-bottom: 2rem;
		text-align: justify;
	}

	.guidelines-section {
		margin-top: 2rem;
	}

	.guidelines-title {
		font-size: 1.5rem;
		color: #4a6cf7;
		margin: 0 0 1rem 0;
		font-weight: 600;
	}

	.guidelines-subtitle {
		font-size: 0.9rem;
		color: #aaa;
		margin-bottom: 1.5rem;
	}

	.guidelines-table {
		border: 1px solid #333;
		border-radius: 0.5rem;
		overflow: hidden;
	}

	.table-header {
		display: grid;
		grid-template-columns: 1fr 1fr;
		background: #2a2a2a;
		font-weight: 600;
		color: #fff;
	}

	.table-row {
		display: grid;
		grid-template-columns: 1fr 1fr;
		border-top: 1px solid #333;
	}

	.table-row:hover {
		background: #252525;
	}

	.table-cell {
		padding: 1rem;
		display: flex;
		align-items: center;
	}

	.feature-name {
		color: #ccc;
	}

	.feature-size {
		color: #fff;
		font-weight: 500;
		justify-content: flex-end;
	}

	@media (max-width: 768px) {
		.popup-content {
			padding: 1.5rem;
			margin: 1rem;
		}

		.tech-title {
			font-size: 2rem;
		}

		.table-header,
		.table-row {
			grid-template-columns: 1fr;
		}

		.table-cell {
			padding: 0.75rem;
		}

		.feature-size {
			justify-content: flex-start;
		}
	}
</style>
